// Indicator toggle bar for the chart, styled like the timeframe selector:
// one pill per indicator, selected = shown. Hovering a pill with editable
// parameters (SMA/EMA/BB) drops down its settings right there — click still
// toggles. MACD/RSI/volume render as fixed regions INSIDE the main chart.
import React, { useEffect, useState, useCallback } from 'react';
import { Lock } from 'lucide-react';

const PILL_BASE = 'px-2 py-1 text-[11px] sm:px-3 sm:text-xs font-medium rounded transition-colors duration-150';
const PILL_ON = 'bg-blue-600 text-white shadow';
const PILL_OFF = 'bg-white text-gray-700 hover:bg-gray-100';

// swingPatterns defaults ON (it was always-on before becoming toggleable)
const DEFAULT_ON = new Set(['swingPatterns']);

const TOGGLES = [
  { key: 'sma', label: 'SMA', title: 'Simple moving averages (overlay) — hover to edit periods' },
  { key: 'ema', label: 'EMA', title: 'Exponential moving averages (overlay) — hover to edit periods' },
  { key: 'vwap', label: 'VWAP', title: 'Volume-weighted average price (overlay)' },
  { key: 'bollingerBands', label: 'BB', title: 'Bollinger Bands (overlay) — hover to edit period/σ' },
  { key: 'swingPatterns', label: 'Swing', title: 'Swing points (HH/HL/LH/LL) + candlestick patterns' },
  { key: 'srProfile', label: 'Profile', title: 'Swing volume profile — support/resistance zones (wide bars = heavy volume)' },
  { key: 'macdPane', label: 'MACD', title: 'MACD region inside the chart' },
  { key: 'rsiPane', label: 'RSI', title: 'RSI region inside the chart' },
];

const IndicatorControls = ({ config, onConfigChange, isAuthed = true, onRequestLogin = () => {} }) => {
  const [customConfig, setCustomConfig] = useState(config);
  const [smaInput, setSmaInput] = useState(
    config?.sma?.periods?.join(', ') || '20, 50, 200'
  );
  const [emaInput, setEmaInput] = useState(
    config?.ema?.periods?.join(', ') || '12, 26, 50'
  );

  useEffect(() => {
    setCustomConfig(config);
    setSmaInput(config?.sma?.periods?.join(', ') || '20, 50, 200');
    setEmaInput(config?.ema?.periods?.join(', ') || '12, 26, 50');
  }, [config]);

  const isEnabled = (key) =>
    customConfig?.[key]?.enabled ?? DEFAULT_ON.has(key);

  const toggle = useCallback((key) => {
    const newConfig = { ...customConfig };
    const current = newConfig[key]?.enabled ?? DEFAULT_ON.has(key);
    newConfig[key] = { ...(newConfig[key] || {}), enabled: !current };
    setCustomConfig(newConfig);
    onConfigChange(newConfig);
  }, [customConfig, onConfigChange]);

  const setField = useCallback((indicatorType, field, value) => {
    const newConfig = { ...customConfig };
    if (!newConfig[indicatorType]) {
      newConfig[indicatorType] = {};
    }
    newConfig[indicatorType] = { ...newConfig[indicatorType], [field]: value };
    setCustomConfig(newConfig);
    onConfigChange(newConfig);
  }, [customConfig, onConfigChange]);

  const handlePeriodChange = useCallback((indicatorType, periods) => {
    const newConfig = { ...customConfig };
    if (!newConfig[indicatorType]) {
      newConfig[indicatorType] = { enabled: true };
    }
    newConfig[indicatorType] = { ...newConfig[indicatorType], periods };
    if (!periods || periods.length === 0) {
      newConfig[indicatorType].enabled = false;
    }
    setCustomConfig(newConfig);
    onConfigChange(newConfig);
  }, [customConfig, onConfigChange]);

  const parsePeriods = useCallback((raw) => {
    if (!raw) return [];
    return raw
      .split(',')
      .map(p => parseInt(p.trim(), 10))
      .filter(p => Number.isFinite(p) && p > 0);
  }, []);

  const periodsEditor = (label, value, setValue, indicatorType, placeholder) => (
    <div>
      <label className="text-xs font-medium text-gray-700">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => handlePeriodChange(indicatorType, parsePeriods(value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handlePeriodChange(indicatorType, parsePeriods(value));
        }}
        className="w-full text-xs border border-gray-300 rounded px-2 py-1 mt-1"
        placeholder={placeholder}
      />
      <div className="text-[10px] text-gray-400 mt-0.5">Enter or click away to apply</div>
    </div>
  );

  const bbEditor = (
    <div>
      <label className="text-xs font-medium text-gray-700">Bollinger Bands</label>
      <div className="flex space-x-3 mt-1">
        <div>
          <label className="text-xs text-gray-600">Period</label>
          <input
            type="number"
            value={customConfig.bollingerBands?.period || 20}
            onChange={(e) => setField('bollingerBands', 'period', parseInt(e.target.value))}
            className="w-16 text-xs border border-gray-300 rounded px-1 py-1 block"
            min="1"
          />
        </div>
        <div>
          <label className="text-xs text-gray-600">Std dev</label>
          <input
            type="number"
            value={customConfig.bollingerBands?.stdDev || 2}
            onChange={(e) => setField('bollingerBands', 'stdDev', parseFloat(e.target.value))}
            className="w-16 text-xs border border-gray-300 rounded px-1 py-1 block"
            min="0.5"
            step="0.5"
          />
        </div>
      </div>
    </div>
  );

  // hover dropdowns per pill (desktop); null = no editable params
  const hoverEditors = {
    sma: () => periodsEditor('SMA periods', smaInput, setSmaInput, 'sma', 'e.g., 20, 50, 200'),
    ema: () => periodsEditor('EMA periods', emaInput, setEmaInput, 'ema', 'e.g., 12, 26, 50'),
    bollingerBands: () => bbEditor,
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap items-center gap-1 bg-white p-1 rounded-md shadow-sm border border-gray-200 max-w-[92vw]">
        {TOGGLES.map(({ key, label, title }) => (
          <div key={key} className="relative group">
            <button
              onClick={() => toggle(key)}
              title={title}
              className={`${PILL_BASE} ${isEnabled(key) ? PILL_ON : PILL_OFF}`}
            >
              {label}
            </button>
            {hoverEditors[key] && (
              /* pt-1 keeps the hover chain unbroken between pill and panel;
                 focus-within keeps it open while typing even if the mouse drifts */
              <div className="absolute top-full left-0 pt-1 hidden group-hover:block group-focus-within:block z-40">
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 w-56">
                  {hoverEditors[key]()}
                </div>
              </div>
            )}
          </div>
        ))}
        {/* Logged-out CTA: the entry/TP/SL signals are the login-gated
            "advanced indicators" — they appear on the chart after sign-in. */}
        {!isAuthed && (
          <button
            onClick={() => onRequestLogin('Entry / take-profit / stop signals')}
            title="Log in to unlock the strategy's entry, take-profit and stop signals on the chart"
            className={`${PILL_BASE} flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100`}
          >
            <Lock size={11} /> Log in to unlock signals
          </button>
        )}
      </div>
    </div>
  );
};

export default IndicatorControls;
